import { useEffect, useRef, useState } from 'react';

import { FieldSelectMulti } from '@/components/looker/field-select-multi';

import { ChartType } from '../../entities/chart-type';

type Option<V> = { label: string; value: V };

function optionFromChartType(chartType: ChartType): Option<ChartType> {
  return { label: chartType, value: chartType };
}

export type ChartTypesFilterProps = {
  value: ChartType[];
  onChange: (value: ChartType[]) => void;
  availableChartTypes?: ChartType[];
  isLoading?: boolean;
};

export function ChartTypesFilter({
  value: defaultValue,
  onChange,
  availableChartTypes,
  isLoading = false,
}: ChartTypesFilterProps) {
  const [selectedValue, setSelectedValue] = useState<ChartType[] | undefined>(
    undefined,
  );

  // Track previous availableChartTypes to detect transitions
  const prevAvailableChartTypesRef = useRef<ChartType[] | undefined>(undefined);

  // Auto-clear invalid selections when available chart types transition from non-empty to empty
  useEffect(() => {
    if (availableChartTypes === undefined) {
      // Still loading or not yet determined - don't clear yet
      prevAvailableChartTypesRef.current = undefined;
      return;
    }

    const prevAvailable = prevAvailableChartTypesRef.current;
    const hasTransitionedToEmpty =
      prevAvailable !== undefined &&
      prevAvailable.length > 0 &&
      availableChartTypes.length === 0;

    // Only clear if:
    // 1. We transitioned from having options to no options
    // 2. User hasn't made a temporary selection
    if (hasTransitionedToEmpty && selectedValue === undefined) {
      const currentValue = defaultValue;
      const validSelections = currentValue.filter((chartType) =>
        availableChartTypes.includes(chartType),
      );

      // Only clear if we have invalid selections
      if (validSelections.length !== currentValue.length) {
        onChange(validSelections);
      }
    }

    // Update ref after processing
    prevAvailableChartTypesRef.current = availableChartTypes;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [availableChartTypes, selectedValue]);

  function handleChange(newValue: string[] = []) {
    // INFO: as there is only ChartType value in options={}, we know we only have ChartType here
    setSelectedValue(newValue as ChartType[]);
  }

  function handlePersist() {
    if (!selectedValue) {
      return;
    }

    onChange(selectedValue);
    setSelectedValue(undefined);
  }

  const value = selectedValue ?? defaultValue;

  // Determine if no chart types are available (not loading, but empty array)
  const hasNoAvailableCharts =
    availableChartTypes !== undefined && availableChartTypes.length === 0;
  const isDisabled = isLoading || hasNoAvailableCharts;

  // Use available chart types if provided, otherwise fall back to all chart types
  const chartTypesToShow = availableChartTypes ?? Object.values(ChartType);
  const options: Option<ChartType>[] =
    chartTypesToShow.map(optionFromChartType);

  // Filter current value to only include available chart types
  const filteredValue = value.filter((chartType) =>
    chartTypesToShow.includes(chartType),
  );

  // Determine label and placeholder based on state
  let label = 'Charts';
  let placeholder: string | undefined = undefined;

  if (isLoading) {
    label = 'Charts (loading...)';
  } else if (hasNoAvailableCharts) {
    label = 'Charts';
    placeholder = 'No charts available';
  }

  return (
    <FieldSelectMulti
      className="max-w-[min(300px,10vw)] sm:max-w-[min(400px,15vw)] lg:max-w-[min(500px,20vw)]"
      label={label}
      placeholder={placeholder}
      values={filteredValue}
      options={options}
      onChange={handleChange}
      onClose={handlePersist}
      flex={2}
      autoResize={false}
      disabled={isDisabled}
    />
  );
}
